// Import necessary modules
import React, { useEffect, useRef, ReactNode, useMemo } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../../pages/base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import { useContextPopupPage } from '../popups/PopupPage';
import FormSysteme from './form-systeme';

interface IDescriptionOptions {
  maxLength?: number;
  width?: string | number;
}

interface IGestionAbstractProps<T> {
  isModeSelection?: boolean;
  path: string;
  primaryKey?: string;
  model: new () => T;
  headerField?: keyof T;
  showSystemTab?: boolean;
  additionalColumns?: ReactNode;
  descriptionOptions?: IDescriptionOptions;
  customFormItems?: (selectedItem: T | null) => ReactNode;
  onFocusEditor?: string;
  cacheKey?: string;
}

export default function GestionAbstract<T extends { description: string }>({ isModeSelection, path, primaryKey, model, headerField = "description", showSystemTab = true, additionalColumns, descriptionOptions, customFormItems, onFocusEditor = "description", cacheKey }: IGestionAbstractProps<T>) {
  const formDataSource = useCustomDataSource<T, T>({ path, primaryKey: primaryKey || `id${path}` });
  const { selectedDataRow: selectedItem, setMode, onFieldDataChanged, setMainForm, isFieldModifier, mode, setIsFieldModifier } = formDataSource;
  const formRef = useRef<FormRef>(null);
  const { setSelectedValue } = useContextPopupPage();

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  }, []);

  useEffect(() => {
    if (mode !== Mode.Lecture && onFocusEditor)
      formRef.current?.instance().getEditor(onFocusEditor)?.focus();
  }, [mode]);

  const descriptionEditorOptions = useMemo(() => {
    return descriptionOptions || { maxLength: 200 };
  }, []);

  return (
    <FormListBase data={formDataSource} isModeSelection={isModeSelection}>
      <FormListBaseGrid dataSource={formDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          // Bug : Cancel le mode modifier en Popup
          // if (mode === Mode.Modifier) {
          //   setMode(Mode.Lecture);
          // }

          if (setSelectedValue && e.row?.data) {
            // console.log("e.row.data", e.row.data);
            setSelectedValue(e.row.data);
          }
        }}>
        {additionalColumns
          ? additionalColumns
          : <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        }
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${(selectedItem as any)?.[headerField] || ''}`} ajouterOptions={{ disabled: false }} modifierOptions={{ disabled: !isFieldModifier }} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || mode !== Mode.Lecture ?
        <FormListBaseTab>
          <TabItem title="Informations">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedItem || new model()} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              {customFormItems
                ? customFormItems(selectedItem)
                : <GroupItem>
                  <SimpleItem key={"frm-description"} dataField={"description"} editorOptions={descriptionEditorOptions}>
                    <Label text={"Description"} showColon={false} />
                  </SimpleItem>
                </GroupItem>
              }
            </Form>
          </TabItem>
          {showSystemTab && (
            <TabItem title="Système">
              <FormSysteme formData={selectedItem || new model()} />
            </TabItem>
          )}
        </FormListBaseTab> : null}
    </FormListBase>
  );
}
